import { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google'
import ReCAPTCHA from 'react-google-recaptcha'
import { Button } from '../../../shared/components/ui/Button/Button'
import { Input } from '../../../shared/components/ui/Input/Input'
import { Icon } from '../../../shared/components/ui/Icon/Icon'
import { useLogin } from '../hooks/UseLogin'
import { useGoogleLogin } from '../hooks/UseGoogleLogin'
import { useMicrosoftLogin } from '../hooks/UseMicrosoftLogin'
import { useToast } from '../../../shared/context/ToastContext'
import { useTheme } from '../../../shared/context/ThemeContext'
import { useBrand } from '../../../shared/context/BrandContext'
import { config } from '../../../config/env'

const RECAPTCHA_SITE_KEY = config.RECAPTCHA_SITE_KEY
const GOOGLE_CLIENT_ID = config.GOOGLE_CLIENT_ID
const MICROSOFT_CLIENT_ID = config.MICROSOFT_CLIENT_ID
const MICROSOFT_TENANT_ID = config.MICROSOFT_TENANT_ID

export default function LoginForm({
  fromLaunch = false,
  identityRefused = false,
}: {
  fromLaunch?: boolean;
  identityRefused?: boolean;
}) {
  const { t } = useTranslation()
  const { effectiveTheme } = useTheme()
  const { brand, getLogo } = useBrand()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const { login, isLoading, error } = useLogin()
  const { googleLogin, isLoading: isGoogleLoading } = useGoogleLogin()
  const { isLoading: isMicrosoftLoading } = useMicrosoftLogin()
  const { showWarning } = useToast()
  const oauthRef = useRef<HTMLDivElement>(null)
  const [googleButtonWidth, setGoogleButtonWidth] = useState(400)

  const updateGoogleWidth = useCallback(() => {
    if (oauthRef.current) {
      const w = oauthRef.current.offsetWidth
      if (w > 0) setGoogleButtonWidth(Math.min(w, 400))
    }
  }, [])

  useEffect(() => {
    updateGoogleWidth()
    const ro = new ResizeObserver(updateGoogleWidth)
    if (oauthRef.current) ro.observe(oauthRef.current)
    return () => ro.disconnect()
  }, [updateGoogleWidth])

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!captchaToken) {
      showWarning(t('auth.completeCaptcha'))
      return
    }
    
    login(email, password, captchaToken)
    
    // Reset captcha after submission
    recaptchaRef.current?.reset()
    setCaptchaToken(null)
  }

  const handleGoogleSuccess = (credentialResponse: any) => {
    if (credentialResponse.credential) {
      googleLogin(credentialResponse.credential)
    }
  }

  const handleGoogleError = () => {
    console.error('Google Login Failed')
  }

  const handleMicrosoftLogin = () => {
    const redirectUri = `${window.location.origin}/azure/auth/callback`
    
    const params = new URLSearchParams({
      client_id: MICROSOFT_CLIENT_ID,
      response_type: 'code',
      redirect_uri: redirectUri,
      response_mode: 'query',
      scope: 'openid profile email User.Read offline_access',
      prompt: 'select_account'
    })

    const authUrl = `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID}/oauth2/v2.0/authorize?${params.toString()}`
    
    window.location.href = authUrl
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <form onSubmit={handleSubmit} className="login-form">
        <div className="login-logo-container">
          <img src={getLogo('iso', 'light')} alt={brand.displayName} className="login-logo" />
        </div>
        <div className="login-welcome">
          <span className="login-welcome-text">{t('auth.welcomeHello')} {t('auth.welcomeBack')}</span> 
        </div>

        {fromLaunch && (
          <div className="launch-notice">
            {t(identityRefused ? 'auth.launchIdentityRefused' : 'auth.launchSignInNeeded')}
          </div>
        )}

        <div className="oauth-buttons" ref={oauthRef}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={config.MODE !== 'development'}
            theme={effectiveTheme === 'dark' ? 'filled_black' : 'outline'}
            size="large"
            width={googleButtonWidth}
          />
          
          <Button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={isLoading || isGoogleLoading || isMicrosoftLoading}
            className="microsoft-button"
          >
            <Icon name="microsoft" size={20} />
            <span>{t('auth.continueWithMicrosoft')}</span>
          </Button>
        </div>

        <div className="oauth-divider">
          <span>{t('auth.continueWith')}</span>
        </div>
      
      {error && <div className="error">{error}</div>}
      
      <div className="input-group">
        {/* <label className="input-label">{t('auth.emailLabel')}</label> */}
        <div className="input-with-icon">
          <Icon name="mail" size={18} className="input-icon" />
          <Input
            type="email"
            placeholder={t('auth.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>
      
      <div className="input-group">
        {/* <label className="input-label">{t('auth.passwordLabel')}</label> */}
        <div className="input-with-icon">
          <Icon name="key" size={18} className="input-icon" />
          <Input
            type="password"
            placeholder={t('auth.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      </div>
      
      {RECAPTCHA_SITE_KEY && (
        <div className="recaptcha-container">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
            theme={effectiveTheme === 'dark' ? 'dark' : 'light'}
          />
        </div>
      )}
      
      <Button type="submit" disabled={isLoading || isGoogleLoading || isMicrosoftLoading || !captchaToken}>
        {isLoading ? t('common.loading') : t('auth.enterButton')}
      </Button>
      
      <Link to="/forgot-password" className="forgot-password-link">
        {t('auth.forgotPassword')}
      </Link>
      </form>
    </GoogleOAuthProvider>
  )
}